import { environment } from '../../../environments/environment';

/**
 * The values that differ per hosted environment. Everything else about the
 * build is a compile-time fact. One image serves dev, staging and production
 * because these are read from /config.json at startup, not baked in.
 */
export interface RuntimeConfig {
    apiBaseUrl: string;
    apiVersion: string;
    keycloakUrl: string;
    keycloakRealm: string;
    keycloakClientId: string;
    /**
     * Realm holding back-office (platform) identities, and its SPA client. Empty
     * where the environment has no platform realm, which keeps the back office
     * unreachable rather than pointed at a realm that does not exist.
     */
    platformRealm: string;
    platformClientId: string;
}

const RUNTIME_KEYS: readonly (keyof RuntimeConfig)[] = [
    'apiBaseUrl',
    'apiVersion',
    'keycloakUrl',
    'keycloakRealm',
    'keycloakClientId',
    'platformRealm',
    'platformClientId',
];

//#region Feature flags, which arrive in the same file
let features: Readonly<Record<string, boolean>> = {};

/**
 * Whether a capability is switched on in this environment. Off unless
 * config.json says otherwise, so an older container serving a config with no
 * `features` object cannot half-release anything.
 * Design: docs/superpowers/specs/2026-09-30-feature-flags-design.md.
 */
export function isFeatureEnabled(flag: string): boolean {
    return features[flag] === true;
}
//#endregion

/**
 * Overlays /config.json onto the compiled defaults in `environment`. Must
 * finish before the app bootstraps (see main.ts). Never throws: a missing or
 * unreadable file means "keep the compiled defaults", which is what `ng serve`
 * relies on.
 */
export async function loadRuntimeConfig(
    fetchFn: typeof fetch = (input, init) => fetch(input, init),
): Promise<void> {
    //#region Fetching the environment's config.json, tolerating its absence
    let served: Partial<Record<keyof RuntimeConfig, unknown>>;
    try {
        const response = await fetchFn('/config.json', { cache: 'no-store' });
        if (!response.ok) {
            return;
        }
        served = await response.json();
    } catch {
        return;
    }
    //#endregion

    //#region Overlaying only the known runtime keys onto the compiled defaults
    for (const key of RUNTIME_KEYS) {
        const value = served?.[key];
        if (typeof value === 'string' && value.length > 0) {
            environment[key] = value;
        }
    }
    //#endregion

    //#region Taking the flags, keeping only the ones that are exactly true
    // Assigned unconditionally: a config.json with no features means every flag
    // is off, which is what an older container serving an older file must mean.
    const supplied = (served as { features?: unknown })?.features;
    features = Object.freeze(
        supplied && typeof supplied === 'object'
            ? Object.fromEntries(
                  Object.entries(supplied as Record<string, unknown>).map(([flag, on]) => [flag, on === true]),
              )
            : {},
    );
    //#endregion
}
